const ROWS = [
  { where: "Homepage tiles (\"What we orchestrate\")", tab: "Capabilities / Products",
    hint: "Edit the 8 capability tiles on the homepage. Title, short headline, expanded detail, icon, color, tile size — all from here." },
  { where: "Notification email (contact@wisewit.ai)", tab: "Site appearance",
    hint: "Scroll to the 'Notification email' card. Type any email + Save. All Project Router + Knowledge AI lead alerts go there from then on." },
  { where: "Footer · Products column (hr.wisewit.ai, cortex.wisewit.ai…)", tab: "Site appearance",
    hint: "Scroll to the 'Footer · Products column' card. Add, edit, or remove any link. Saves are live immediately." },
  { where: "Weekly / daily digest email", tab: "Site appearance",
    hint: "Scroll to 'Automated digest email'. Toggle on, pick daily or weekly, optionally click 'Send a test digest now' to preview." },
  { where: "Brand colors, fonts, font size", tab: "Site appearance",
    hint: "Change every color and font on the site in real-time. Hit 'Reset' if you don't like a change." },
  { where: "Reels (short videos on /reels)", tab: "Reels",
    hint: "Add / edit / delete. Upload a cover image and a .mp4 video — preview shows up inline so you can verify." },
  { where: "Case studies on /case-studies", tab: "Case studies",
    hint: "Each case study needs an industry, the problem, the solution, the outcome, tech stack, and 2-4 impact metrics." },
  { where: "Systems layers (the 7 stack layers on /systems)", tab: "Systems layers",
    hint: "Edit the 'Capture → Context → Inference → Orchestration → Action → Analytics → Governance' layer titles, descriptions, and tags." },
  { where: "Playbooks on /playbooks (Plants, Inspection, Operations…)", tab: "Playbooks",
    hint: "Each playbook has 4 lists: challenges, workflows, what-you-build, outcomes. Edit them as plain bullet lines." },
  { where: "Knowledge Centre articles + LinkedIn posts", tab: "Knowledge",
    hint: "Add articles. For a LinkedIn post: fill title + summary, leave body empty, paste the LinkedIn URL into 'External link', pick 'LinkedIn' as Source. It appears on the homepage 'From LinkedIn' section AND on the Knowledge page." },
  { where: "Careers / Positions on /careers", tab: "Careers",
    hint: "Add / edit positions. Departments sync live from hr.wisewit.ai automatically." },
  { where: "Uploaded files (images / videos) + fix production 404/403s", tab: "Files",
    hint: "See every file on the server, probe whether each public URL is reachable, and grab the exact Nginx snippet if uploads aren't loading in production." },
  { where: "Who visited the site (visitor analytics)", tab: "Analytics",
    hint: "Monthly total + unique visitors, top countries, top pages, last 50 visits." },
  { where: "Form submissions, newsletter, Knowledge AI leads", tab: "Inbox",
    hint: "All contact submissions and email captures land here." },
];

export default function GuidePanel({ setTab }) {
  return (
    <div data-testid="guide-panel">
      <div className="ww-card p-6 md:p-10">
        <div className="text-overline mb-4">◢ Where to edit what · Wisewit Admin guide</div>
        <h2 className="font-display text-3xl text-white">Quick map of the dashboard.</h2>
        <p className="mt-3 text-[#8E8E93] max-w-3xl text-[15px] leading-relaxed">
          Click any row to jump straight to the right tab. Everything you see on <code className="text-[#DFFF00]">wisewit.ai</code> can be edited from here — no code changes needed.
        </p>
      </div>

      <div className="mt-8 border border-[#2A2A2A]">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">
          <div className="col-span-5">What you want to change</div>
          <div className="col-span-2">Tab</div>
          <div className="col-span-5">How</div>
        </div>
        {ROWS.map((r, i) => (
          <button
            key={i}
            onClick={() => setTab(r.tab)}
            data-testid={`guide-row-${r.tab.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="w-full text-left grid grid-cols-12 gap-4 px-5 py-4 border-t border-[#2A2A2A] hover:bg-[#0a0a0a] transition-colors group"
          >
            <div className="col-span-5 text-white font-display text-base group-hover:text-[#DFFF00]">{r.where}</div>
            <div className="col-span-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[#FF4500]">{r.tab} →</div>
            <div className="col-span-5 text-[#8E8E93] text-sm leading-relaxed">{r.hint}</div>
          </button>
        ))}
      </div>

      <div className="mt-8 ww-card p-6 text-[12px] text-[#8E8E93] leading-relaxed">
        <div className="text-overline mb-3">◢ Two shortcuts you&apos;ll use often</div>
        <ul className="list-disc ml-5 space-y-2">
          <li>
            <strong className="text-white">Change the email all alerts go to:</strong> Site appearance → Notification email card.
          </li>
          <li>
            <strong className="text-white">Edit the homepage products / tiles:</strong> Capabilities / Products tab.
            Each tile has a Title, short headline, expanded detail, icon, accent color, and size.
          </li>
        </ul>
      </div>

      <div className="mt-4 ww-card p-6 text-[12px] text-[#8E8E93] leading-relaxed border-l-4 border-[#DFFF00]">
        <div className="text-overline mb-3 text-[#DFFF00]">◢ Nginx setting for Cloudflare Stream uploads</div>
        <p className="mb-3">
          When you upload a video to Cloudflare Stream from the admin, the bytes travel through wisewit.ai first
          (this bypasses corporate networks / VPNs that reject direct connections to <code className="text-[#DFFF00]">upload.cloudflarestream.com</code>).
          For that to work, the <code className="text-white">/api/</code> Nginx location needs a bigger body limit.
        </p>
        <pre className="bg-[#050505] border border-[#2A2A2A] p-3 font-mono text-[10px] text-white leading-relaxed overflow-x-auto">location /api/ &#123;
    proxy_pass http://127.0.0.1:8011;
    proxy_http_version 1.1;
    proxy_set_header Host $host;
    proxy_set_header X-Real-IP $remote_addr;
    proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
    proxy_set_header X-Forwarded-Proto $scheme;
    proxy_read_timeout 600s;   # ← was 120s (uploads need this)
    proxy_send_timeout 600s;   # ←
    client_max_body_size 500m; # ← was 25M (videos need this)
&#125;</pre>
        <p className="mt-3">
          Then <code className="text-white">sudo nginx -t &amp;&amp; sudo systemctl reload nginx</code>. Nothing else changes — the direct-to-Cloudflare path is no longer used.
        </p>
      </div>

      <div className="mt-4 ww-card p-6 text-[12px] text-[#8E8E93] leading-relaxed border-l-4 border-[#FF4500]">
        <div className="text-overline mb-3 text-[#FF4500]">◢ Deploying on your production VM after `git pull`</div>
        <p className="mb-3">
          The <strong className="text-white">two most common surprises</strong> after pulling new code from GitHub:
        </p>
        <ol className="list-decimal ml-5 space-y-2">
          <li>
            <strong className="text-white">The frontend needs to be rebuilt.</strong> `git pull` only updates source files — your
            browser is served the compiled bundle. Run this on the VM after every pull:
            <pre className="mt-1 bg-[#050505] border border-[#2A2A2A] p-2 font-mono text-[11px] text-white">cd frontend && yarn install && yarn build && sudo systemctl reload nginx</pre>
          </li>
          <li>
            <strong className="text-white">The backend needs to be restarted.</strong> New Python routes only take effect after a restart:
            <pre className="mt-1 bg-[#050505] border border-[#2A2A2A] p-2 font-mono text-[11px] text-white">source backend/venv/bin/activate &amp;&amp; pip install -r backend/requirements.txt &amp;&amp; sudo systemctl restart wisewit-backend</pre>
          </li>
          <li>
            <strong className="text-white">Purge Cloudflare cache</strong> for <code className="text-[#DFFF00]">*.js</code> and <code className="text-[#DFFF00]">/api/*</code>.
            Otherwise visitors keep loading yesterday&apos;s JS bundle.
          </li>
        </ol>
        <p className="mt-3">
          If <em>Delete</em> in Files tab still returns 405 after `git pull`, it&apos;s always step 1 — the browser is running an
          older JS bundle that still uses the DELETE method. The new code uses POST.
        </p>
      </div>
    </div>
  );
}
